import { NumberButtonType } from '@components/PropertiesSearch/PropertySearchFilters';
import { SearchFilterData } from '@components/PropertiesSearch/utils';
import { BedsBathsSection } from '@components/FilterComponents/BedsBathsSection';
import { ButtonRow } from '@components/FilterComponents/ButtonRow';
import {
  CheckboxSection,
  CheckboxSectionWithArrayOfObject,
} from '@components/FilterComponents/CheckboxSection';
import { ForSaleSection } from '@components/FilterComponents/ForSaleSection';
import { PriceSection } from '@components/FilterComponents/PriceSection';
import { TextSection } from '@components/FilterComponents/TextSection';
import { YearSection } from '@components/FilterComponents/YearSection';
import {
  LifestyleVerticalEnum,
  ParkingFeatureEnum,
  PropertySubTypeEnum,
  PropertyTypeEnum,
  StandardStatusEnum,
  ViewEnum,
} from '@engelvoelkersamericas/api';
import { useMeasurement } from '@util/customHooks/useMeasurement';
import isObjectEmpty from '@util/isObjectEmpty';
import { Dayjs } from 'dayjs';
import { ChangeEvent } from 'react';
import { FieldType } from '../../PropertySearchFilters';
import {
  AllFiltersWrapper,
  ModalHeader,
  ModalSection,
  SecondHeader,
  SectionDivider,
} from '../../PropertySearchFilters/styles';
import { useTranslations } from 'next-intl';

export interface AllFiltersSectionProps {
  hideRentFilters?: boolean;
  searchFilterData: SearchFilterData;
  isDivider?: boolean;
  minPrice?: number;
  maxPrice?: number;
  maxAllowedPrice?: number;
  priceInterval?: number;
  maxAllowedSqft?: number;
  maxAllowedLotSize?: number;
  minSqft?: number;
  maxSqft?: number;
  minLotSize?: number;
  maxLotSize?: number;
  minYear?: number;
  maxYear?: number;
  minStories?: number;
  maxStories?: number;
  maxAllowedStories?: number;
  selectedPropertyTypes: PropertySubTypeEnum[];
  selectedListingTypes: PropertyTypeEnum[];
  selectedGarage: {
    vehicles?: number;
    type?: ParkingFeatureEnum[];
  };
  selectedBedrooms?: number;
  selectedBathrooms?: number;
  selectedViews: ViewEnum[];
  selectedAmenities: string[];
  selectedLifeStyle: LifestyleVerticalEnum[];
  selectedStates?: string[];
  selectedProvinces?: string[];
  selectedListingStatus: StandardStatusEnum[];
  handleDateChange: (d: Dayjs | null, first?: boolean) => void;
  handleSetSelectedFilter: (f: string) => void;
  handleSetArr: (type: string, b: string | string[]) => void;
  handleNumberButtonChange: (type: NumberButtonType, value: number | null) => void;
  handleSliderChange: (event: Event, newValue: number | number[]) => void;
  handleTextFieldChange: (e: ChangeEvent<HTMLInputElement>, field: FieldType, min?: 'min') => void;
}
export const AllFiltersSection = ({
  searchFilterData,
  minPrice,
  maxPrice,
  maxAllowedPrice,
  priceInterval,
  minSqft,
  maxSqft,
  maxAllowedSqft,
  minLotSize,
  maxLotSize,
  maxAllowedLotSize,
  minYear,
  maxYear,
  minStories,
  maxStories,
  maxAllowedStories,
  selectedBedrooms,
  selectedBathrooms,
  selectedPropertyTypes,
  selectedListingTypes,
  selectedGarage,
  selectedViews,
  selectedAmenities,
  selectedLifeStyle,
  selectedStates,
  selectedProvinces,
  selectedListingStatus,
  isDivider,
  handleSetArr,
  handleSliderChange,
  handleTextFieldChange,
  handleNumberButtonChange,
  handleDateChange,
  hideRentFilters,
}: AllFiltersSectionProps) => {
  const t = useTranslations();
  const { preferredMeasurement } = useMeasurement();
  const recreationRanchFilters = searchFilterData.lifestyle.ranch?.filter((vertical) =>
    (vertical.value as string).includes('Recreation'),
  );
  const workingRanchFilters = searchFilterData.lifestyle.ranch?.filter((vertical) =>
    (vertical.value as string).includes('Working'),
  );

  return (
    <AllFiltersWrapper>
      {searchFilterData.propertyTypes && (
        <ForSaleSection
          propertyTypes={searchFilterData.propertyTypes}
          listingTypes={searchFilterData.listingTypes}
          handleSetCheckbox={handleSetArr}
          selectedPropertyTypes={selectedPropertyTypes}
          selectedListingTypes={selectedListingTypes}
          hideRentFilters={hideRentFilters}
        />
      )}
      {searchFilterData.priceSection && (
        <PriceSection
          minPrice={minPrice}
          maxPrice={maxPrice}
          handleSliderChange={handleSliderChange}
          maxAllowedPrice={maxAllowedPrice}
          priceInterval={priceInterval}
          handleTextFieldChange={handleTextFieldChange}
        />
      )}
      {searchFilterData.bathrooms && searchFilterData.bedrooms && (
        <BedsBathsSection
          bedrooms={searchFilterData.bedrooms}
          bathrooms={searchFilterData.bathrooms}
          bedsValue={selectedBedrooms}
          bathsValue={selectedBathrooms}
          onBedsChange={(val) => handleNumberButtonChange('bedrooms', val)}
          onBathroomsChange={(val) => handleNumberButtonChange('bathrooms', val)}
        />
      )}
      {searchFilterData.listingStatus && (
        <ModalSection>
          <ModalHeader>{t('Listing Status')}</ModalHeader>
          <CheckboxSectionWithArrayOfObject
            items={searchFilterData.listingStatus}
            handleSetSelected={handleSetArr}
            selectedArr={selectedListingStatus}
            type="listing_status"
          />
        </ModalSection>
      )}
      {searchFilterData.sqftSection && (
        <ModalSection>
          <ModalHeader>
            {t(`Square ${preferredMeasurement === 'sq.ft' ? 'Feet' : 'Meters'}`)}
          </ModalHeader>
          <TextSection
            minVal={minSqft}
            maxVal={maxSqft ?? maxAllowedSqft}
            handleTextFieldChange={handleTextFieldChange}
            field={'sq_feet'}
          />
        </ModalSection>
      )}
      {searchFilterData.lotSizeSection && (
        <ModalSection>
          <ModalHeader>{t('Lot Size')}</ModalHeader>
          <TextSection
            minVal={minLotSize}
            maxVal={maxLotSize ?? maxAllowedLotSize}
            handleTextFieldChange={handleTextFieldChange}
            field={'lot_size'}
          />
        </ModalSection>
      )}
      {!isObjectEmpty(searchFilterData.lifestyle) ? (
        <>
          <SectionDivider />
          <SecondHeader>Lifestyle</SecondHeader>
          <ModalSection>
            {searchFilterData.lifestyle.water?.length && (
              <>
                <ModalHeader>Water</ModalHeader>
                <CheckboxSectionWithArrayOfObject
                  items={searchFilterData.lifestyle.water}
                  handleSetSelected={handleSetArr}
                  selectedArr={selectedLifeStyle}
                  type="life_styles"
                />
              </>
            )}
            {searchFilterData.lifestyle.golf?.length && (
              <>
                <ModalHeader>Golf</ModalHeader>
                <CheckboxSectionWithArrayOfObject
                  items={searchFilterData.lifestyle.golf}
                  handleSetSelected={handleSetArr}
                  selectedArr={selectedLifeStyle}
                  type="life_styles"
                />
              </>
            )}
            {searchFilterData.lifestyle.mountain?.length && (
              <>
                <ModalHeader>Mountain</ModalHeader>
                <CheckboxSectionWithArrayOfObject
                  items={searchFilterData.lifestyle.mountain}
                  handleSetSelected={handleSetArr}
                  selectedArr={selectedLifeStyle}
                  type="life_styles"
                />
              </>
            )}
            {searchFilterData.lifestyle.wine?.length && (
              <>
                <ModalHeader>Wine</ModalHeader>
                <CheckboxSectionWithArrayOfObject
                  items={searchFilterData.lifestyle.wine}
                  handleSetSelected={handleSetArr}
                  selectedArr={selectedLifeStyle}
                  type="life_styles"
                />
              </>
            )}
            {recreationRanchFilters && (
              <>
                <ModalHeader>Recreational Ranch</ModalHeader>
                {searchFilterData.lifestyle.ranch?.length && (
                  <CheckboxSectionWithArrayOfObject
                    items={recreationRanchFilters}
                    handleSetSelected={handleSetArr}
                    selectedArr={selectedLifeStyle}
                    type="life_styles"
                  />
                )}
              </>
            )}

            {workingRanchFilters && (
              <>
                <ModalHeader>Working Ranch</ModalHeader>
                <CheckboxSectionWithArrayOfObject
                  items={workingRanchFilters}
                  handleSetSelected={handleSetArr}
                  selectedArr={selectedLifeStyle}
                  type="life_styles"
                />
              </>
            )}
          </ModalSection>
        </>
      ) : (
        <></>
      )}
      {isDivider && (
        <>
          <SectionDivider />
          <SecondHeader>{t('Property Features')}</SecondHeader>
        </>
      )}
      {(searchFilterData.garage.vehicles || searchFilterData.garage.type) && (
        <ModalSection>
          <ModalHeader>{t('Garage')}</ModalHeader>
          {searchFilterData.garage.vehicles && (
            <ButtonRow
              buttons={searchFilterData.garage.vehicles}
              value={selectedGarage.vehicles}
              onChange={(value) => handleNumberButtonChange('vehicles', value)}
            />
          )}
          {searchFilterData.garage.type && (
            <CheckboxSectionWithArrayOfObject
              items={searchFilterData.garage.type}
              handleSetSelected={handleSetArr}
              selectedArr={selectedGarage.type}
              type="type"
            />
          )}
        </ModalSection>
      )}
      {searchFilterData.views && (
        <ModalSection>
          <ModalHeader>{t('Views')}</ModalHeader>
          <CheckboxSection
            items={searchFilterData.views}
            handleSetSelected={handleSetArr}
            selectedArr={selectedViews}
            type="views"
          />
        </ModalSection>
      )}
      {searchFilterData.storiesSection && (
        <ModalSection>
          <ModalHeader>{t('Stories')}</ModalHeader>
          <TextSection
            minVal={minStories}
            maxVal={maxStories ?? maxAllowedStories}
            handleTextFieldChange={handleTextFieldChange}
            field={'stories'}
          />
        </ModalSection>
      )}
      {searchFilterData.yearsSection && (
        <YearSection valArr={[minYear, maxYear]} handleDateChange={handleDateChange} />
      )}
      {searchFilterData.amenities && (
        <ModalSection>
          <ModalHeader>{t('Amenities')}</ModalHeader>
          <CheckboxSection
            items={searchFilterData.amenities}
            handleSetSelected={handleSetArr}
            selectedArr={selectedAmenities}
            type="amenities"
          />
        </ModalSection>
      )}
      {searchFilterData.states && (
        <ModalSection>
          <ModalHeader>{t('State (US)')}</ModalHeader>
          <CheckboxSection
            items={searchFilterData.states}
            handleSetSelected={handleSetArr}
            selectedArr={selectedStates ?? []}
            type="states"
          />
        </ModalSection>
      )}
      {searchFilterData.provinces && (
        <ModalSection>
          <ModalHeader>{t('Province (CAN)')}</ModalHeader>
          <CheckboxSection
            items={searchFilterData.provinces}
            handleSetSelected={handleSetArr}
            selectedArr={selectedProvinces ?? []}
            type="provinces"
          />
        </ModalSection>
      )}
    </AllFiltersWrapper>
  );
};
